/**
 * The personal SOS (FM Technician B41, D23) — hold-to-send.
 *
 *   SosButton — the primitive: press and HOLD for two seconds; a ring fills
 *     round the button as you hold, and the words say "Hold to send SOS".
 *     Let go early and nothing is sent ("Keep holding to send"). Pointer
 *     (finger, mouse) and keyboard (hold Space or Enter) alike. 48 px and up.
 *   TechSos — the top bar's SOS slot (hidden until R3, interpretation 86):
 *     the button, and once sent a panel with what was sent ("Last checked in
 *     at Tower A at 22:04 IST") and the answer — "Waiting for your FM or
 *     shift lead", then "Seen by Lena Patil at 22:06 IST". While one is open
 *     a tap opens that panel; a second hold would be the same SOS anyway.
 *
 * It goes to the site's FMs and the shift lead on shift there only, never
 * building-wide (the panel says so). Offline it cannot go: the button says
 * "You're offline — call your FM" instead of pretending — as its words from
 * sm up, and on a phone (ring and bell only, 48 px) as the hint a press shows.
 *
 * Calls (maintenance:work): GET /tech/sos, POST /tech/sos.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellAlertIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { apiPostEnvelope } from '@/utils/api';
import { FmSheet, useOnline } from '@/components/fm/ticketParts';
import { Pill, serverWords } from '@/components/Tech/techParts';
import { phoneNow } from '@/components/Tech/safetyApi';
import { EMERGENCY_KEYS, useMySos, type SosView } from '@/components/Tech/emergencyApi';

/** How long the button must be held. */
export const HOLD_MS = 2000;
const TICK_MS = 50;
export const HOLD_WORDS = 'Hold to send SOS';
export const OFFLINE_SOS = "You're offline — call your FM";
export const EARLY_HINT = 'Keep holding for 2 seconds to send. Nothing was sent.';

export interface SosButtonProps {
  onSend: () => void;
  /** Why it cannot be sent now (offline, sending), or null. */
  disabledWhy?: string | null;
  /** The top bar's small form: below sm just the ring and the bell (48 px —
   *  the bar has about 15 px to spare at 360 px), the words from sm up. */
  compact?: boolean;
  /** A short press (let go early): the caller may show a hint. */
  onEarlyRelease?: () => void;
  /** A press while it cannot send: the caller shows the reason (on a phone
   *  the compact button has no room for it). */
  onBlockedPress?: (why: string) => void;
  testid?: string;
}

export function SosButton({
  onSend, disabledWhy = null, compact = false, onEarlyRelease, onBlockedPress, testid = 'sos-button',
}: SosButtonProps) {
  const [progress, setProgress] = useState(0);
  const started = useRef<number | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const fire = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (tick.current) clearInterval(tick.current);
    if (fire.current) clearTimeout(fire.current);
    tick.current = null; fire.current = null; started.current = null;
  };
  useEffect(() => clear, []);

  const begin = () => {
    if (disabledWhy) { onBlockedPress?.(disabledWhy); return; }
    if (started.current !== null) return;
    started.current = Date.now();
    setProgress(0);
    tick.current = setInterval(() => {
      if (started.current === null) return;
      setProgress(Math.min(1, (Date.now() - started.current) / HOLD_MS));
    }, TICK_MS);
    fire.current = setTimeout(() => {
      clear();
      setProgress(0);
      onSend();
    }, HOLD_MS);
  };
  const end = () => {
    if (started.current === null) return;
    clear();
    setProgress(0);
    onEarlyRelease?.();
  };
  // A hold whose release never arrives (the phone locks, the app goes to the
  // background, alt-tab) is let go, not sent: only a hold kept for the whole
  // two seconds sends.
  const endRef = useRef(end);
  endRef.current = end;
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'hidden') endRef.current(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const r = compact ? 20 : 30;
  const circ = 2 * Math.PI * r;
  const size = (r + 4) * 2;
  return (
    <button
      type="button"
      data-testid={testid}
      data-progress={progress.toFixed(2)}
      aria-label={disabledWhy ? `SOS: ${disabledWhy}` : `${HOLD_WORDS} (2 seconds) to your FM and shift lead`}
      title={disabledWhy ?? HOLD_WORDS}
      aria-disabled={!!disabledWhy}
      onPointerDown={(e) => { e.preventDefault(); begin(); }}
      onPointerUp={end}
      onPointerLeave={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
      onBlur={end}
      onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); begin(); } }}
      onKeyUp={(e) => { if (e.key === ' ' || e.key === 'Enter') end(); }}
      onContextMenu={(e) => e.preventDefault()}
      className={`relative inline-flex shrink-0 select-none items-center gap-2 whitespace-nowrap rounded-full border-2 border-danger-600 font-semibold text-danger-700 [touch-action:none] ${
        compact ? 'h-12 w-12 min-h-12 min-w-12 justify-center px-0 text-sm sm:h-auto sm:w-auto sm:justify-start sm:px-2'
          : 'min-h-16 px-5 text-lg'} ${
        disabledWhy ? 'cursor-not-allowed opacity-60' : 'bg-surface hover:bg-danger-50'}`}
    >
      <span className="relative inline-flex items-center justify-center" style={{ width: size - 8, height: size - 8 }}
            aria-hidden="true">
        <svg width={size - 8} height={size - 8} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="4" className="stroke-danger-100" />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="4" strokeLinecap="round"
                  className="stroke-danger-600" strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)}
                  data-testid={`${testid}-ring`} />
        </svg>
        <BellAlertIcon className={compact ? 'h-4 w-4' : 'h-6 w-6'} />
      </span>
      {compact ? (
        // Below sm the ring and bell alone (the name is in aria-label); from
        // sm the words — and when it cannot send, why.
        <span className="hidden sm:inline" data-testid={`${testid}-words`}>{disabledWhy ?? HOLD_WORDS}</span>
      ) : <span>{disabledWhy ?? (progress > 0 ? 'Keep holding…' : HOLD_WORDS)}</span>}
    </button>
  );
}

export function SosStatus({ sos, message }: { sos: SosView; message: string | null }) {
  return (
    <div className="space-y-3" data-testid="sos-status">
      {message && <p className="text-base font-medium text-content" data-testid="sos-message">{message}</p>}
      <p className="text-base text-content">SOS sent at {sos.raised_label}.</p>
      <p className="text-base text-content-muted" data-testid="sos-place">{sos.place}</p>
      {sos.acknowledged
        ? <Pill tone="green" word={sos.seen_label ?? 'Seen'} icon={CheckCircleIcon} testid="sos-seen" wrap />
        : <Pill tone="amber" word="Waiting for your FM or shift lead" testid="sos-waiting" />}
      {!sos.acknowledged && (
        <p className="text-sm text-content-muted">If nobody answers, call your FM. Stay where you are if it is safe.</p>
      )}
    </div>
  );
}

export function TechSos() {
  const online = useOnline();
  const qc = useQueryClient();
  const mine = useMySos(true);
  const [panel, setPanel] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => apiPostEnvelope<SosView>('/tech/sos', { client_at: phoneNow() }),
    onSuccess: (r) => {
      qc.setQueryData(EMERGENCY_KEYS.sos, r.data);
      setMessage(r.message ?? null); setError(null); setPanel(true);
      void qc.invalidateQueries({ queryKey: EMERGENCY_KEYS.sos });
    },
    onError: (e) => {
      setError(serverWords(e, 'The SOS did not go. Call your FM now.')); setMessage(null); setPanel(true);
    },
  });
  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => setHint(null), 3000);
    return () => clearTimeout(t);
  }, [hint]);
  const open = mine.data && !mine.data.acknowledged ? mine.data : null;
  const why = !online ? OFFLINE_SOS : send.isPending ? 'Sending…' : null;
  // After a failed send, an older SOS that was already answered must not read
  // as "SOS sent at … Seen by …" — only one still waiting, or one this send
  // returned, is shown.
  const shown: SosView | null = error
    ? (send.data?.data ?? open)
    : (mine.data ?? send.data?.data ?? null);
  // A press that cannot send says why in words (offline: call your FM) — on
  // a phone the compact button has no room for them (interpretation 216).
  return (
    <div className="relative" data-testid="tech-sos">
      {open ? (
        <button type="button" data-testid="sos-open"
                className="inline-flex h-12 w-12 min-h-12 min-w-12 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-danger-600 px-0 text-sm font-semibold text-white sm:h-auto sm:w-auto sm:px-3"
                aria-label="SOS sent. See who has seen it." onClick={() => setPanel(true)}>
          <BellAlertIcon className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">SOS sent</span>
        </button>
      ) : (
        <SosButton compact onSend={() => send.mutate()} disabledWhy={why}
                   onEarlyRelease={() => setHint(EARLY_HINT)} onBlockedPress={(w) => setHint(w)} />
      )}
      {hint && (
        <p role="status" data-testid="sos-hint"
           className="absolute right-0 top-full z-20 mt-1 w-56 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface p-2 text-sm text-content shadow-lg">
          {hint}
        </p>
      )}
      <FmSheet open={panel} onClose={() => setPanel(false)} tech testid="sos-panel" title="SOS"
               subtitle="To your FM and the shift lead on shift at your site. Not a building alarm.">
        {error && <p role="alert" className="text-base text-danger-700" data-testid="sos-error">{error}</p>}
        {shown && <SosStatus sos={shown} message={message} />}
        <button type="button" className="btn-secondary tech-tap mt-3" onClick={() => setPanel(false)}>Close</button>
      </FmSheet>
    </div>
  );
}
